<template>
  <div class="custom-upload-qr-code-sound-wave">
    <div v-show="customizationTemplate.options.values.length > 1">
      <div class="customization-title">
        {{ customizationTemplate.options.title }}
      </div>
      <sib-select
        class="mb-sm"
        size="md"
        :options="customizationTemplate.options.values"
        :value="optionSelected"
        @input="changeOption"
      ></sib-select>
    </div>

    <template
      v-if="customizationTemplate.spotify_song || customizationTemplate.youtube"
    >
      <CustomSearchSong
        v-show="
          optionSelected == customizationTemplate.spotify_song?.value ||
          optionSelected == customizationTemplate.youtube?.value
        "
        v-bind="{
          title:
            optionSelected == customizationTemplate.youtube?.value
              ? customizationTemplate.youtube?.title
              : customizationTemplate.spotify_song?.title,
          placeholderInput:
            optionSelected == customizationTemplate.youtube?.value
              ? customizationTemplate.youtube?.placeholder
              : customizationTemplate.spotify_song?.placeholder,
          soundWave: !!customizationTemplate.sound_wave,
          qrCode: !!customizationTemplate.qr_code,
          qrCodeTemplate: customizationTemplate.qr_code?.template,
          selectImageButtonCustomily: getInputByType(QR_CODE),
          appName:
            optionSelected == customizationTemplate.youtube?.value
              ? 'youtube'
              : 'spotify',
        }"
        @change-spotify-image-qrcode="fillUpDataCustomily($event, QR_CODE)"
        @change-spotify-song-selected="handleSpotifySongSelected"
        @remove-spotify-song-selected="removeImage"
      >
      </CustomSearchSong>
    </template>

    <template v-if="customizationTemplate.audio_button">
      <CustomUploadAudio
        v-show="optionSelected == customizationTemplate.audio_button.value"
        v-bind="{
          id,
          title: customizationTemplate.audio_button.title,
          soundWave: !!customizationTemplate.sound_wave,
          qrCode: !!customizationTemplate.qr_code,
          qrCodeTemplate: customizationTemplate.qr_code?.template,
          selectImageButtonCustomily: getInputByType(QR_CODE),
        }"
        @change-audio-image-qrcode="fillUpDataCustomily($event, QR_CODE)"
        @remove-audio-file="removeImage"
      ></CustomUploadAudio>
    </template>

    <template v-if="customizationTemplate.qr_code">
      <CustomQrCode
        :class="{
          'mt-sm': customizationTemplate.qr_code?.template.color_picker,
        }"
        v-bind="{
          template: customizationTemplate.qr_code?.template,
        }"
      />
    </template>
    <template v-if="customizationTemplate.sound_wave">
      <CustomSoundWave
        :class="{
          'mt-sm': customizationTemplate.sound_wave?.template.color_picker,
        }"
        v-bind="{
          soundWaveWidth: customizationTemplate.sound_wave.width,
          soundWaveHeight: customizationTemplate.sound_wave.height,
          barThickness: customizationTemplate.sound_wave.bar_thickness,
          spaceBetweenBar: customizationTemplate.sound_wave.space_between_bar,
          barHeight: customizationTemplate.sound_wave.bar_height,
          template: customizationTemplate.sound_wave?.template,
        }"
        @change-image-sound-wave="fillUpDataCustomily($event, SOUND_WAVE)"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import CustomQrCode from './CustomQrCode.vue'
import CustomSoundWave from './CustomSoundWave.vue'
import CustomUploadAudio from './CustomUploadAudio.vue'
import CustomSearchSong from './CustomSearchSong.vue'
import { useProductCustomization } from './composables/ProductCustomization'
import {
  CUSTOMILY_FILE_INPUT_WRAPPER_CLASS,
  CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR,
  INPUT_FILE_CUSTOMILY_SELECTOR,
  INPUT_TEXT_CUSTOMILY_SELECTOR,
  REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR,
} from '@/utils'

const {
  convertImageLinkToFileList,
  changeFileImageCustomily,
  changeInputCustomily,
} = useProductCustomization()

const props = withDefaults(
  defineProps<{
    id: string
    customizationTemplate: any
  }>(),
  {}
)
const optionSelected = ref(props.customizationTemplate?.options.values[0].value)
const SOUND_WAVE = 'soundWave'
const QR_CODE = 'qrCode'
const IMAGE_TYPE = 'image'

onMounted(() => {
  props.customizationTemplate.type_sort_ids.forEach((item: any) => {
    if (item.hidden) {
      hiddenOptionWrapperBySortId(item.sort_id)
    }
  })
})

function handleSpotifySongSelected(songSelected: any) {
  const artistSong = songSelected.artist
  const nameSong = songSelected.name
  if (artistSong) {
    fillUpDataCustomily(artistSong, 'artist')
  }
  if (nameSong) {
    fillUpDataCustomily(nameSong, 'song')
  }
}

async function fillUpDataCustomily(value: string, type: string) {
  if (!value || !type) return
  const typeSortIdItem = props.customizationTemplate.type_sort_ids.find(
    (item: any) => item.value == type
  )
  if (!typeSortIdItem) return
  const inputCustomily = getInputCustomilyBySortId(
    typeSortIdItem.sort_id,
    typeSortIdItem.type
  )
  if (!inputCustomily) return
  if (typeSortIdItem.type == IMAGE_TYPE) {
    const imageFileList = await convertImageLinkToFileList(value, type)
    changeFileImageCustomily(imageFileList, inputCustomily)
  } else if (typeSortIdItem.type == 'text') {
    changeInputCustomily(value, inputCustomily)
  }
}

function getInputByType(type: string) {
  const sortIdItem = props.customizationTemplate.type_sort_ids.find(
    (item: any) => item.value == type
  )
  if (!sortIdItem) return
  return getInputCustomilyBySortId(sortIdItem.sort_id, IMAGE_TYPE)
}

function getInputCustomilyBySortId(sortId: any, type: string) {
  const customilyOptionWrapper = document.querySelector(
    `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
      'option_sort_id',
      sortId
    )}`
  )
  if (!customilyOptionWrapper) return

  if (type == IMAGE_TYPE) {
    const customilyFileInputWrapper = customilyOptionWrapper.querySelector(
      `.${CUSTOMILY_FILE_INPUT_WRAPPER_CLASS}`
    )
    if (!customilyFileInputWrapper) return

    return customilyFileInputWrapper.querySelector(
      INPUT_FILE_CUSTOMILY_SELECTOR
    )
  }

  if (type == 'text') {
    return customilyOptionWrapper.querySelector(INPUT_TEXT_CUSTOMILY_SELECTOR)
  }
}

function getRemoveButtonCustomilyBySortId(sortId: any) {
  const customilyOptionWrapper = document.querySelector(
    `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
      'option_sort_id',
      sortId
    )}`
  )
  if (!customilyOptionWrapper) return
  const customilyFileInputWrapper = customilyOptionWrapper.querySelector(
    `.${CUSTOMILY_FILE_INPUT_WRAPPER_CLASS}`
  )
  if (!customilyFileInputWrapper) return

  return customilyFileInputWrapper.querySelector(
    REMOVE_IMAGE_BUTTON_CUSTOMILY_SELECTOR
  )
}

function hiddenOptionWrapperBySortId(sortId: any) {
  const customilyOptionWrapper = document.querySelector(
    `.${CUSTOMILY_OPTION_SORT_ID_WRAPPER_SELECTOR.replace(
      'option_sort_id',
      sortId
    )}`
  )
  if (!customilyOptionWrapper) return
  customilyOptionWrapper.style.display = 'none'
}

function changeOption(value: string) {
  optionSelected.value = value
  removeImage()
}

function removeImage() {
  getRemoveButtonCustomilyBySortId
  const imageSortIdItems = props.customizationTemplate.type_sort_ids.filter(
    (item: any) => item.type == IMAGE_TYPE
  )
  if (!imageSortIdItems.length) return
  imageSortIdItems.forEach(
    (imageSortIdItem: { type: string; sort_id: number }) => {
      const removeButtonCustomily = getRemoveButtonCustomilyBySortId(
        imageSortIdItem.sort_id
      )
      if (removeButtonCustomily) (removeButtonCustomily as HTMLElement).click()
    }
  )
}
</script>
